import * as React from 'react'
import { cn } from '@/lib/utils'

const Toast = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { variant?: 'default' | 'destructive' }
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all',
        variant === 'default' && 'border bg-background text-foreground',
        variant === 'destructive' && 'destructive group border-destructive bg-destructive text-destructive-foreground',
        className
      )}
      {...props}
    />
  )
})
Toast.displayName = 'Toast'

const ToastTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h4 ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
  )
)
ToastTitle.displayName = 'ToastTitle'

const ToastDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm opacity-90', className)} {...props} />
  )
)
ToastDescription.displayName = 'ToastDescription'

const Toaster = () => {
  const [toasts, setToasts] = React.useState<Array<{ id: number; title: string; description?: string; variant?: 'default' | 'destructive' }>>([])

  React.useEffect(() => {
    window.addEventListener('toast', ((e: CustomEvent) => {
      const newToast = { id: Date.now(), ...e.detail }
      setToasts(prev => [...prev, newToast])
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== newToast.id))
      }, 4000)
    }) as EventListener)
  }, [])

  return (
    <div className="fixed top-20 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm">
      {toasts.map(toast => (
        <Toast key={toast.id} variant={toast.variant}>
          <div className="grid gap-1">
            <ToastTitle>{toast.title}</ToastTitle>
            {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
          </div>
        </Toast>
      ))}
    </div>
  )
}

export const toast = (detail: { title: string; description?: string; variant?: 'default' | 'destructive' }) => {
  window.dispatchEvent(new CustomEvent('toast', { detail }))
}

export { Toast, ToastTitle, ToastDescription, Toaster }
